import { ApiError } from '@batiflow/client-core';
import { Button, Caption1, __css, tokens } from '@fluentui/react-components';
import { Copy20Regular, ErrorCircle24Regular } from '@fluentui/react-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FullPageState } from './full-page-state';

const useStyles = __css({"details":{"a9b677":"fly5x3f","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f9b8bpc","sj55zd":"fkfq4zb"},"summary":{"Bceei9c":"f1k6fduh","Bhrd7zp":"fl43uef"},"lines":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f778t4r","Bo78w4l":0,"Cgygva":0,"yil37a":"f183lrzt","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f9b8bpc","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f12n3xlw","De3pzq":"f16xq7d1","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"ft85np5","Btd35i7":"fjksvth"}});

export interface ErrorPageProps {
  error: unknown;
  /** Called by the "retry" button; reloads the page when absent. */
  onRetry?: () => void;
}

/**
 * Plain-language error page. The technical reference (trace id, version) is only inside the
 * "Technical details" disclosure, with a copy button so people can send it to support.
 */
export function ErrorPage({ error, onRetry }: ErrorPageProps) {
  const { t } = useTranslation(['backoffice', 'common']);
  const styles = useStyles();
  const [copy, setCopy] = useState<'idle' | 'done' | 'failed'>('idle');

  const traceId = error instanceof ApiError ? error.traceId : undefined;
  const lines = [
    t('common:errors.traceId', { traceId: traceId ?? t('backoffice:error.noTrace') }),
    t('backoffice:error.release', { release: __BP_RELEASE__ }),
  ];

  const copyDetails = async () => {
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopy('done');
    } catch {
      setCopy('failed');
    }
  };

  return (
    <FullPageState
      role="alert"
      icon={<ErrorCircle24Regular />}
      title={t('backoffice:error.title')}
      actions={
        <Button
          appearance="primary"
          onClick={
            onRetry ??
            (() => {
              window.location.reload();
            })
          }
        >
          {onRetry ? t('common:actions.retry') : t('backoffice:error.reload')}
        </Button>
      }
    >
      {t('backoffice:error.body')}
      <details className={styles.details}>
        <summary className={styles.summary}>{t('backoffice:error.details')}</summary>
        <div className={styles.lines}>
          {lines.map((line) => (
            <Caption1 key={line}>{line}</Caption1>
          ))}
        </div>
        <Button
          size="small"
          icon={<Copy20Regular />}
          onClick={() => {
            void copyDetails();
          }}
        >
          {t('common:actions.copyDetails')}
        </Button>
        <div role="status" aria-live="polite">
          {copy === 'done' ? <Caption1>{t('backoffice:error.copied')}</Caption1> : null}
          {copy === 'failed' ? <Caption1>{t('backoffice:error.copyFailed')}</Caption1> : null}
        </div>
      </details>
    </FullPageState>
  );
}


import "/opt/batiflow/app/apps/backoffice/src/shared/components/error-page.griffel.css";